import { Component } from '@theme/component';
import { isClickedOutside, normalizeString, onAnimationEnd } from '@theme/utilities';

/**
 * A custom element that displays a localization form.
 *
 * @typedef {object} FormRefs
 * @property {HTMLDivElement} countryList - The country list element.
 * @property {HTMLInputElement} countryInput - The country input element.
 * @property {HTMLUListElement[]} countryListItems - The country list items element.
 * @property {HTMLFormElement} form - The form element.
 * @property {HTMLDivElement} liveRegion - The live region element.
 * @property {HTMLSelectElement} languageInput - The language input element.
 * @property {HTMLSpanElement} noResultsMessage - The no results message element.
 * @property {HTMLUListElement} popularCountries - The popular countries element.
 * @property {HTMLInputElement} search - The search input element.
 * @property {HTMLButtonElement} resetButton - The reset button element.
 *
 * @extends {Component<FormRefs>}
 */
class LocalizationFormComponent extends Component {
  connectedCallback() {
    super.connectedCallback();

    if (this.dataset.useGtranslate === 'true' && this.refs.languageInput) {
      const savedLang = localStorage.getItem('theme_lang_switcher');
      if (savedLang) {
        this.refs.languageInput.value = savedLang;
        setTimeout(() => this.resizeLanguageInput(), 0);
      }
    }

    this.refs.search && this.refs.search.addEventListener('keydown', this.#onSearchKeyDown);
    this.refs.countryList && this.refs.countryList.addEventListener('keydown', this.#onContainerKeyDown);
    this.refs.countryList && this.refs.countryList.addEventListener('scroll', this.#onCountryListScroll);

    // Resizing the language input can be expensive for browsers that don't support field-sizing: content.
    // Spliting it into separate tasks at least helps when there are multiple localization forms on the page.
    setTimeout(() => this.resizeLanguageInput(), 0);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.refs.search && this.refs.search.removeEventListener('keydown', this.#onSearchKeyDown);
    this.refs.countryList && this.refs.countryList.removeEventListener('keydown', this.#onContainerKeyDown);
    this.refs.countryList && this.refs.countryList.removeEventListener('scroll', this.#onCountryListScroll);
  }

  /**
   * Handles the keydown event for the container.
   *
   * @param {KeyboardEvent} event - The event object.
   */
  #onContainerKeyDown = (event) => {
    const { countryInput, countryListItems, form } = this.refs;

    switch (event.key) {
      case 'ArrowUp':
        event.preventDefault();
        event.stopPropagation();
        this.#changeCountryFocus('UP');
        break;
      case 'ArrowDown':
        event.preventDefault();
        event.stopPropagation();
        this.#changeCountryFocus('DOWN');
        break;
      case 'Enter': {
        event.preventDefault();
        event.stopPropagation();
        const focusedItem = countryListItems.find((item) => item.getAttribute('aria-selected') === 'true');

        if (focusedItem) {
          countryInput.value = focusedItem.dataset.value ?? '';
          form.submit();
        }
        break;
      }
    }

    if (!this.refs.search) return;

    setTimeout(() => {
      const focusableItems = this.refs.countryListItems.filter((item) => !item.hasAttribute('hidden'));
      const focusedItemIndex = focusableItems.findIndex((item) => item === document.activeElement);
      const focusedItem = focusableItems[focusedItemIndex];

      if (focusedItem) {
        this.refs.search.setAttribute('aria-activedescendant', focusedItem.id);
      } else {
        this.refs.search.setAttribute('aria-activedescendant', '');
      }
    });
  };

  /**
   * Selects a country.
   *
   * @param {string} countryName - The name of the country to select.
   * @param {Event} event - The event object.
   */
  selectCountry = (countryName, event) => {
    event.preventDefault();
    const { countryInput, form } = this.refs;

    countryInput.value = countryName;
    form?.submit();
  };

  /**
   * Changes the language of the localization form.
   *
   * @param {Event} event - The event object.
   */
  changeLanguage(event) {
    const { form, languageInput } = this.refs;
    const value = event.target instanceof HTMLSelectElement ? event.target.value : null;

    if (!value) return;

    languageInput.value = value;
    this.resizeLanguageInput();

    const useGTranslate = this.dataset.useGtranslate === 'true';
    const sourceLanguage = this.dataset.sourceLanguage || 'it';

    if (useGTranslate) {
      const runGTranslate = () => {
        if (typeof window.doGTranslate !== 'function') return false;

        try {
          window.doGTranslate(`${sourceLanguage}|${value}`);
          localStorage.setItem('theme_lang_switcher', value);
          document.documentElement.setAttribute('lang', value);
          return true;
        } catch (error) {
          console.warn('GTranslate error:', error);
          return false;
        }
      };

      if (runGTranslate()) return;

      let attempts = 0;
      const retry = window.setInterval(() => {
        attempts += 1;
        if (runGTranslate() || attempts >= 20) {
          window.clearInterval(retry);

          if (attempts >= 20) {
            form.submit();
          }
        }
      }, 150);

      return;
    }

    form.submit();
  }

  resizeLanguageInput() {
    const { languageInput } = this.refs;

    if (!languageInput || CSS.supports('field-sizing', 'content')) return;

    // Hide all options except the selected option
    for (const option of languageInput.options) {
      if (!option.selected) {
        option.dataset.optionLabel = option.textContent || '';
        option.innerText = '';
      }
    }

    // Calculate the width of the select element (which is based on the width of the widest option)
    languageInput.style.width = 'fit-content';
    const originalElementWidth = `${Math.ceil(languageInput.offsetWidth) + 1}px`;

    // Fix the width of the select element
    if (languageInput.offsetWidth > 0) {
      languageInput.style.width = originalElementWidth;
    }

    // Add back all option labels
    for (const option of languageInput.options) {
      if (option.dataset.optionLabel) {
        option.textContent = option.dataset.optionLabel;
        delete option.dataset.optionLabel;
      }
    }
  }

  /**
   * Finds matches for a given search value in a country element.
   *
   * @typedef {Object} Options
   * @property {boolean} [matchLabel] - Whether to match the label.
   * @property {boolean} [matchAlias] - Whether to match the alias.
   * @property {boolean} [matchIso] - Whether to match the iso.
   * @property {boolean} [matchCurrency] - Whether to match the currency.
   * @property {boolean} [labelMatchStart] - Whether to match the label start.
   * @property {boolean} [aliasExactMatch] - Whether to match the alias exact match.
   *
   * @typedef {Object} MatchTypes
   * @property {boolean} [label] - Whether the label matches the search value.
   * @property {boolean} [alias] - Whether the alias matches the search value.
   * @property {boolean} [iso] - Whether the iso matches the search value.
   * @property {boolean} [currency] - Whether the currency matches the search value.
   *
   * @param {string} searchValue
   * @param {HTMLElement} countryElement
   * @param {Options} options
   * @returns {MatchTypes}
   */
  #findCountryMatches(searchValue, countryElement, options = {}) {
    const label = countryElement.getAttribute('aria-label')?.toLowerCase() || '';
    const aliases = countryElement.dataset.aliases?.toLowerCase() || '';
    const iso = countryElement.dataset.value?.toLowerCase() || '';
    const currency = countryElement.querySelector('.localization-form__currency')?.textContent?.toLowerCase() || '';

    const normalizedSearch = normalizeString(searchValue);
    const normalizedLabel = normalizeString(label);
    const normalizedAliases = normalizeString(aliases);
    const normalizedIso = normalizeString(iso);
    const normalizedCurrency = normalizeString(currency);

    return {
      label: options.matchLabel
        ? options.labelMatchStart
          ? normalizedLabel.startsWith(normalizedSearch)
          : normalizedLabel.includes(normalizedSearch)
        : false,
      alias: options.matchAlias
        ? options.aliasExactMatch
          ? normalizedAliases.split(',').includes(normalizedSearch)
          : normalizedAliases.includes(normalizedSearch)
        : false,
      iso: options.matchIso ? normalizedIso.includes(normalizedSearch) : false,
      currency: options.matchCurrency ? normalizedCurrency.includes(normalizedSearch) : false,
    };
  }

  /**
   * Filters the countries based on the search input.
   */
  filterCountries = () => {
    const { countryListItems, noResultsMessage, popularCountries, resetButton, search } = this.refs;
    if (!search) return;

    const searchValue = search.value.trim().toLowerCase();
    const searchLength = searchValue.length;
    let visibleItems = 0;

    search.closest('.country-filter')?.classList.toggle('is-searching', searchLength > 0);
    resetButton.hidden = searchLength === 0;

    if (searchLength === 0) {
      this.resetCountriesFilter();
      return;
    }

    for (const item of countryListItems) {
      const exactMatches = this.#findCountryMatches(searchValue, item, {
        matchLabel: true,
        matchAlias: true,
        labelMatchStart: true,
        aliasExactMatch: true,
      });

      const partialMatches = this.#findCountryMatches(searchValue, item, {
        matchLabel: true,
        matchAlias: true,
        matchIso: true,
        matchCurrency: true,
      });

      const shouldShow =
        exactMatches.label ||
        exactMatches.alias ||
        (!exactMatches.label &&
          !exactMatches.alias &&
          (partialMatches.label || partialMatches.alias || partialMatches.iso || partialMatches.currency));

      item.hidden = !shouldShow;

      if (shouldShow) {
        visibleItems += 1;
      }
    }

    if (popularCountries) {
      const visiblePopularCountries = popularCountries.querySelectorAll('.localization-form__list-item:not([hidden])').length;
      popularCountries.hidden = visiblePopularCountries === 0;
    }

    noResultsMessage.hidden = visibleItems > 0;

    if (this.refs.liveRegion) {
      const countLabel = this.dataset.labelResultsCount?.replace('[count]', `${visibleItems}`) || '';
      this.refs.liveRegion.textContent = countLabel;
    }
  };

  /**
   * Resets the countries filter.
   */
  resetCountriesFilter = () => {
    const { countryListItems, noResultsMessage, popularCountries, resetButton, search } = this.refs;
    if (!search) return;

    search.value = '';
    search.closest('.country-filter')?.classList.remove('is-searching');
    resetButton.hidden = true;

    for (const item of countryListItems) {
      item.hidden = false;
      item.removeAttribute('aria-selected');
    }

    if (popularCountries) {
      popularCountries.hidden = false;
    }

    noResultsMessage.hidden = true;
    search.setAttribute('aria-activedescendant', '');
  };

  /**
   * Changes the focus of the country list.
   *
   * @param {'UP' | 'DOWN'} direction
   */
  #changeCountryFocus(direction) {
    const { countryListItems } = this.refs;
    const focusableItems = countryListItems.filter((item) => !item.hasAttribute('hidden'));
    if (focusableItems.length === 0) return;

    const currentIndex = focusableItems.findIndex((item) => item.getAttribute('aria-selected') === 'true');
    let nextIndex = 0;

    if (direction === 'UP') {
      nextIndex = currentIndex > 0 ? currentIndex - 1 : focusableItems.length - 1;
    } else if (direction === 'DOWN') {
      nextIndex = currentIndex < focusableItems.length - 1 ? currentIndex + 1 : 0;
    }

    for (const item of countryListItems) {
      item.removeAttribute('aria-selected');
    }

    const nextItem = focusableItems[nextIndex];
    nextItem.setAttribute('aria-selected', 'true');
    nextItem.focus();
    nextItem.scrollIntoView({ block: 'nearest' });
  }

  /**
   * Handles keydown on search input.
   *
   * @param {KeyboardEvent} event
   */
  #onSearchKeyDown = (event) => {
    switch (event.key) {
      case 'ArrowUp':
      case 'ArrowDown':
        event.preventDefault();
        event.stopPropagation();
        this.#changeCountryFocus(event.key === 'ArrowUp' ? 'UP' : 'DOWN');
        break;
      case 'Escape':
        this.resetCountriesFilter();
        break;
    }
  };

  /**
   * Handles country list scroll.
   */
  #onCountryListScroll = () => {
    const { countryList } = this.refs;
    const countryFilter = this.querySelector('.country-filter');

    if (!countryList || !countryFilter) return;

    countryFilter.classList.toggle('is-scrolled', countryList.scrollTop > 0);
  };
}

if (!customElements.get('localization-form-component')) {
  customElements.define('localization-form-component', LocalizationFormComponent);
}

/**
 * Dropdown localization component
 */
class DropdownLocalizationComponent extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('click', this.#onClick);
    document.addEventListener('click', this.#onDocumentClick);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('click', this.#onClick);
    document.removeEventListener('click', this.#onDocumentClick);
  }

  #onClick = (event) => {
    if (!(event.target instanceof HTMLElement)) return;
    const button = event.target.closest('[ref="button"]');

    if (button) {
      event.preventDefault();
      this.toggleSelector();
    }
  };

  #onDocumentClick = (event) => {
    if (!isClickedOutside(event, this)) return;
    if (!this.refs.panel || this.refs.panel.hidden) return;
    this.hideSelector();
  };

  toggleSelector() {
    if (!this.refs.panel) return;

    if (this.refs.panel.hidden) {
      this.showSelector();
    } else {
      this.hideSelector();
    }
  }

  showSelector() {
    if (!this.refs.panel || !this.refs.button) return;

    this.refs.panel.hidden = false;
    this.refs.button.setAttribute('aria-expanded', 'true');
  }

  hideSelector() {
    if (!this.refs.panel || !this.refs.button) return;

    this.refs.button.setAttribute('aria-expanded', 'false');

    onAnimationEnd(this.refs.panel, () => {
      this.refs.panel.hidden = true;
    });
  }
}

if (!customElements.get('dropdown-localization-component')) {
  customElements.define('dropdown-localization-component', DropdownLocalizationComponent);
}